'use client';

import { RestApiPaymentMethodDto } from '@order-mage/integration-sdk';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ComponentPropsWithoutRef, useCallback, useContext, useEffect } from 'react';
import { useWatch } from 'react-hook-form';

import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';

import { isAvailableNow } from '@/entities/venue/lib/useTimeAvailability';
import { QueryApi } from '@/features/query/api';
import { cn } from '@/shared/lib/cn';
import { useSelectedCurrency } from '@/shared/lib/hooks/useSelectedCurrency';
import { Icon } from '@/shared/ui/icon';

import { Card } from '@/shared/ui/card';
import { Form } from '@/shared/ui/form';
import { CardPaymentMethodsBoxItem } from './cart-payment-methods-box-item';
import './cart-payment-methods-box.scss';

import { useCartStore, CartContext, getPriceValueTranslation } from '@/entities/cart';

type Props = ComponentPropsWithoutRef<typeof Card>;
export const CartPaymentMethodsBox = (props: Props) => {
    const t = useTranslations();
    const getPaymentMethodPrice = useCartStore((s) => s.getPaymentMethodPrice);
    const getLocalizedValue = useGetLocalizedValue();
    const paymentMethodsQuery = useQuery(QueryApi.PaymentMethods.queries.PaymentMethods);
    const deliveryMethodsQuery = useQuery(QueryApi.DeliveryMethods.queries.DeliveryMethods);
    const [selectedCurrency] = useSelectedCurrency();
    const getMinOrderRestriction = useCartStore((s) => s.getMinOrderRestriction);
    const { formMethods } = useContext(CartContext);
    const selectedPaymentMethodId = useWatch({
        control: formMethods.control,
        name: 'paymentMethod.id',
    });
    const selectedDeliveryMethodId = useWatch({
        control: formMethods.control,
        name: 'deliveryMethod.id',
    });

    const isRestrictedByDeliveryMethod = useCallback(
        (pm: RestApiPaymentMethodDto) => {
            if (!pm.deliveryMethodRestrictions?.length || !selectedDeliveryMethodId) return false;
            return !pm.deliveryMethodRestrictions.some(
                (r) => r.deliveryMethodId === selectedDeliveryMethodId,
            );
        },
        [selectedDeliveryMethodId],
    );

    const getDeliveryRestrictionLabel = useCallback(
        (pm: RestApiPaymentMethodDto) => {
            if (!pm.deliveryMethodRestrictions?.length || !deliveryMethodsQuery.data) return undefined;
            const allowedNames = pm.deliveryMethodRestrictions
                .map((r) => deliveryMethodsQuery.data?.find((dm) => dm.id === r.deliveryMethodId))
                .filter(Boolean)
                .map((dm) => getLocalizedValue(dm!.name));
            if (!allowedNames.length) return undefined;
            return t('CART_PAYMENT_METHODS_BOX.DELIVERY_METHOD_ONLY', { methods: allowedNames.join(', ') });
        },
        [deliveryMethodsQuery.data, getLocalizedValue, t],
    );

    useEffect(() => {
        if (!selectedPaymentMethodId || !paymentMethodsQuery.data) return;
        const selectedMethod = paymentMethodsQuery.data.find((m) => m.id === selectedPaymentMethodId);
        if (!selectedMethod) return;
        const isTimeRestricted = !isAvailableNow(selectedMethod.timeAvailability ?? []);
        if (
            isRestrictedByDeliveryMethod(selectedMethod) ||
            getMinOrderRestriction(selectedMethod).isDisabled ||
            isTimeRestricted
        ) {
            formMethods.setValue('paymentMethod.id', null);
        }
    }, [
        selectedPaymentMethodId,
        paymentMethodsQuery.data,
        isRestrictedByDeliveryMethod,
        getMinOrderRestriction,
        formMethods,
    ]);

    return (
        <Card
            {...props}
            className={cn('cart-payment-methods-box', props.className)}
            data-testid="cart-payment-methods-box"
        >
            <h3 className="cart-payment-methods-box__title" data-testid="cart-payment-methods-title">
                {t('CART_PAYMENT_METHODS_BOX.TITLE')}
            </h3>
            <Form.RadioGroup
                name="paymentMethod.id"
                className="cart-payment-methods-box__radio-group"
                data-testid="cart-payment-methods-list"
            >
                {({ value }) => (
                    <>
                        {paymentMethodsQuery.data?.map((method) => {
                            const { minPrice, isDisabled: isMinOrderDisabled } =
                                getMinOrderRestriction(method);
                            const isDeliveryRestricted = isRestrictedByDeliveryMethod(method);
                            const isTimeRestricted = !isAvailableNow(method.timeAvailability ?? []);
                            const disabled = isMinOrderDisabled || isDeliveryRestricted || isTimeRestricted;
                            const minOrderLabel = minPrice
                                ? t('CART_PAYMENT_METHODS_BOX.MIN_ORDER_VALUE', {
                                      price: getPriceValueTranslation({
                                          amount: parseFloat(minPrice.amount),
                                          currency: selectedCurrency,
                                          t,
                                      }),
                                  })
                                : undefined;
                            const deliveryRestrictionLabel = isDeliveryRestricted
                                ? getDeliveryRestrictionLabel(method)
                                : undefined;
                            const timeRestrictionLabel = isTimeRestricted
                                ? t('CART_PAYMENT_METHODS_BOX.TIME_RESTRICTED')
                                : undefined;

                            return (
                                <CardPaymentMethodsBoxItem
                                    key={method.id}
                                    id={method.id}
                                    icon={(svgProps) => (
                                        <Icon
                                            name={
                                                method.definition?.icon === 'coins' ? 'coins' : 'credit-card'
                                            }
                                            {...svgProps}
                                        />
                                    )}
                                    name={method.name}
                                    isIconPrimary={true}
                                    isActive={value === method.id}
                                    price={
                                        getPaymentMethodPrice(
                                            method as Parameters<typeof getPaymentMethodPrice>[0],
                                        ) ?? undefined
                                    }
                                    disabled={disabled}
                                    minOrderLabel={minOrderLabel}
                                    deliveryRestrictionLabel={deliveryRestrictionLabel}
                                    timeRestrictionLabel={timeRestrictionLabel}
                                />
                            );
                        })}
                    </>
                )}
            </Form.RadioGroup>
        </Card>
    );
};
